import {EditorView} from '@codemirror/view'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import type {ImageDimensions} from '@github-ui/image-utils'
import {getImageSizeFromFile, markdownComment, markdownImageTag, markdownLink} from '@github-ui/image-utils'
import {noop} from '@github-ui/noop'
import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import type {FileType, UnifiedFileSelectResult} from '@github-ui/use-unified-file-select'
import {useUnifiedFileSelect} from '@github-ui/use-unified-file-select'
import type React from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'

import {clearDropPosition, getDropPosition} from '../codemirror/extensions/drop-position'
import {
  addUploadPlaceholderEffect,
  removeUploadPlaceholderEffect,
  updateUploadPlaceholderEffect,
} from '../codemirror/extensions/upload-placeholder'
import {isRenderableVideoUrl} from '../language-tools/html/extensions/video-links'
import {getNewlinePrefix, getNewlineSuffix} from '../language-tools/source-utils'

const placeholder = (file: File, id: string) => markdownComment(`Uploading "${file.name}"... [upload-id:${id}]`)
const isImageFile = (file: File) => file.type.startsWith('image/')

const isVideoFile = (file: File, url?: string | null) => {
  if (file.type.startsWith('video/')) return true
  if (url && isRenderableVideoUrl(url)) return true
  return isRenderableVideoUrl(file.name)
}

const normalizeUploadUrl = (url: string | null) => {
  if (!url) return url

  const trimmed = url.trim()
  if (!trimmed) return trimmed

  if (typeof window === 'undefined' || !window.location?.origin) return trimmed

  try {
    return new URL(trimmed, window.location.origin).href
  } catch {
    return trimmed
  }
}

const markdown = (file: File, url: string | null, dimensions?: ImageDimensions | null) => {
  if (!url) return markdownComment(`Failed to upload "${file.name}"`)

  const normalizedUrl = normalizeUploadUrl(url)
  if (!normalizedUrl) return markdownComment(`Failed to upload "${file.name}"`)

  if (isVideoFile(file, normalizedUrl)) return normalizedUrl
  if (isImageFile(file))
    return markdownImageTag(dimensions || {width: 0, height: 0, pixelsPerInch: 1}, normalizedUrl, 'Image')
  return markdownLink(file.name, normalizedUrl)
}

type UploadProgress = [current: number, total: number]

type UseFileHandlingResult = UnifiedFileSelectResult & {
  errorMessage?: React.ReactNode
  uploadProgress?: UploadProgress
  handleFileUpload?: (file: File) => Promise<void>
}

type UseFileHandlingProps = {
  repositoryId?: number
  view: EditorView | null
  disabled?: boolean
  value: string
  onUploadFile?: (file: File, onProgress?: (progressBytes: number) => void) => Promise<FileUploadResult>
  acceptedFileTypes?: FileType[]
  onFilesRejected?: (files: File[], rejectedTypes: string[]) => void
}

export type FileUploadResult = {
  /** The URL of the uploaded file. `null` if the upload failed (or reject the promise). */
  url: string
  /**
   * The file that was uploaded. Typically the client-side detected name, size, and content
   * type can be unreliable, so your file upload service may provide more accurate data. By
   * receiving an updated File instance with the more accurate data, the Markdown editor can
   * make better decisions.
   */
  file: File
}

type OptFileUploadResult = FileUploadResult | {url: null; file: File}

type PreparedUploadEntry = {
  id: string
  file: File
  placeholderText: string
  order: number
  total: number
  previewUrl?: string
  mediaKind?: 'image' | 'video'
}

type UploadPlaceholderAddEffect = ReturnType<(typeof addUploadPlaceholderEffect)['of']>
type UploadPlaceholderUpdateEffect = ReturnType<(typeof updateUploadPlaceholderEffect)['of']>

export const useFileHandling = ({
  value,
  view,
  disabled,
  onUploadFile,
  acceptedFileTypes,
  onFilesRejected,
}: UseFileHandlingProps): UseFileHandlingResult | null => {
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | undefined>(undefined)
  const placeholderEntriesRef = useRef<Map<string, PreparedUploadEntry>>(new Map())
  const placeholderIdRef = useRef(0)

  const viewRef = useRef<EditorView | null>(view)
  useEffect(() => {
    viewRef.current = view
  }, [view])

  useEffect(() => {
    const entries = placeholderEntriesRef.current
    return () => {
      entries.clear()
    }
  }, [])

  const errorVisibleForEnoughTime = useRef(false)
  useEffect(() => {
    if (errorMessage) {
      errorVisibleForEnoughTime.current = false
      const id = setTimeout(() => (errorVisibleForEnoughTime.current = true), 1000)
      return () => clearTimeout(id)
    }
  }, [errorMessage])
  useEffect(() => {
    // clears the error message when the user types and enough time has passed
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (errorVisibleForEnoughTime.current) setErrorMessage(undefined)
  }, [value])

  const safeSetRejectedFiles = useSafeAsyncCallback((files: File[]) => {
    const types = new Set(
      files
        .map(({name}) => {
          const parts = name.split('.')
          return parts.length > 1 ? `.${parts.at(-1)}` : ''
        })
        .filter(s => s !== ''),
    )

    if (types.size > 0) {
      const typesList = [...types].join(', ')
      setErrorMessage(
        <>
          <span>
            File type<span>{types.size > 1 ? 's' : ''}</span> {typesList} not supported. See the{' '}
          </span>
          <a href="https://docs.github.com/en/get-started/writing-on-github/working-with-advanced-formatting/attaching-files">
            documentation
          </a>
          <span> for supported file types.</span>
        </>,
      )
      onFilesRejected?.(files, [...types])
    }
  })

  const [uploadProgress, setUploadProgress] = useState<UploadProgress | undefined>(undefined)

  const updatePlaceholderProgress = useCallback((entryId: string, progressPercent: number | undefined) => {
    const editorView = viewRef.current
    if (!editorView) return

    editorView.dispatch({
      effects: [
        updateUploadPlaceholderEffect.of({
          id: entryId,
          data: {progressPercent},
        }),
      ],
    })
  }, [])

  const syncPlaceholderProgress = useCallback((progress: UploadProgress | undefined) => {
    const editorView = viewRef.current
    if (!editorView) return
    if (placeholderEntriesRef.current.size === 0) return

    const effects: UploadPlaceholderUpdateEffect[] = []
    let percent: number | undefined

    if (progress) {
      const [current, total] = progress
      const completed = total > 0 ? Math.min(total, Math.max(0, current - 1)) : 0
      percent = total > 0 ? Math.round((completed / total) * 100) : 0
    }

    for (const entry of placeholderEntriesRef.current.values()) {
      effects.push(
        updateUploadPlaceholderEffect.of({
          id: entry.id,
          data: {progressPercent: percent},
        }),
      )
    }

    if (effects.length > 0) editorView.dispatch({effects})
  }, [])

  const setInitialUploadProgress = useCallback(
    (total: number) => {
      const initial: UploadProgress = [1, total]
      setUploadProgress(initial)
      syncPlaceholderProgress(initial)
    },
    [syncPlaceholderProgress],
  )

  const incrementUploadProgress = useSafeAsyncCallback(() => {
    setUploadProgress(previous => {
      if (!previous) return previous
      const next: UploadProgress = [previous[0] + 1, previous[1]]
      syncPlaceholderProgress(next)
      return next
    })
  })

  const clearUploadProgress = useSafeAsyncCallback(() => {
    setUploadProgress(undefined)
    syncPlaceholderProgress(undefined)
  })

  const insertPlaceholder = useCallback((entries: PreparedUploadEntry[]) => {
    const editorView = viewRef.current
    if (!editorView || entries.length === 0) return

    // Determine where to insert the placeholder:
    // 1. Check if there's a drop position from a recent drop event (user dragged files)
    // 2. Fall back to the current selection start (user clicked upload button)
    let insertPos = getDropPosition(editorView)
    if (insertPos !== null) {
      // Clear the drop position so it's not reused for subsequent operations
      clearDropPosition(editorView)
    } else {
      // Fall back to the current selection start
      insertPos = editorView.state.selection.main.from
    }

    const doc = editorView.state.doc.toString()
    const newLinePrefix = getNewlinePrefix(doc, insertPos)
    const newLineSuffix = getNewlineSuffix(doc, insertPos)
    const placeholderTexts = entries.map(entry => entry.placeholderText)
    const insertText = `${newLinePrefix}${placeholderTexts.join('\n')}${newLineSuffix}`

    const effects: UploadPlaceholderAddEffect[] = []
    let firstPlaceholderStart: number | undefined
    let cursor = insertPos + newLinePrefix.length

    for (const [index, entry] of entries.entries()) {
      placeholderEntriesRef.current.set(entry.id, entry)

      const start = cursor
      const end = start + entry.placeholderText.length
      if (firstPlaceholderStart === undefined) firstPlaceholderStart = start

      effects.push(
        addUploadPlaceholderEffect.of({
          id: entry.id,
          from: start,
          to: end,
          data: {
            id: entry.id,
            fileName: entry.file.name,
            mimeType: entry.file.type,
            order: entry.order,
            total: entry.total,
            progressPercent: 0,
            previewUrl: entry.previewUrl,
            mediaKind: entry.mediaKind,
          },
        }),
      )

      cursor = end
      if (index < entries.length - 1) cursor += 1
    }

    editorView.dispatch({
      changes: {from: insertPos, to: insertPos, insert: insertText},
      selection: {anchor: insertPos + insertText.length},
      scrollIntoView: true,
      effects,
    })
    if (firstPlaceholderStart !== undefined) {
      editorView.dispatch({effects: [EditorView.scrollIntoView(firstPlaceholderStart, {y: 'nearest'})]})
    }
    editorView.focus()
  }, [])

  const replacePlaceholderWithMarkdown = useCallback(
    (entry: PreparedUploadEntry, result: OptFileUploadResult, dimensions: ImageDimensions | null) => {
      const editorView = viewRef.current
      if (!editorView) return

      const doc = editorView.state.doc.toString()
      const placeholderIndex = doc.indexOf(entry.placeholderText)
      if (placeholderIndex === -1) {
        placeholderEntriesRef.current.delete(entry.id)
        return
      }

      const replacement = markdown(result.file, result.url, dimensions)

      editorView.dispatch({
        changes: {
          from: placeholderIndex,
          to: placeholderIndex + entry.placeholderText.length,
          insert: replacement,
        },
        selection: {anchor: placeholderIndex + replacement.length},
        scrollIntoView: true,
        effects: [removeUploadPlaceholderEffect.of({id: entry.id})],
      })
      editorView.focus()

      // Clean up blob URL
      if (entry.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl)
      }

      placeholderEntriesRef.current.delete(entry.id)
    },
    [],
  )

  // It's crucial that this is done safely because file uploads can take a long time - there's
  // a very good chance that the references will be outdated or the component unmounted by the time this is called.
  const safeHandleCompletedFileUpload = useSafeAsyncCallback(
    (entry: PreparedUploadEntry, result: OptFileUploadResult, dimensions: ImageDimensions | null) => {
      incrementUploadProgress()

      if (result.url) {
        // eslint-disable-next-line no-console
        console.info('Uploaded file available at:', result.url)
      }

      replacePlaceholderWithMarkdown(entry, result, dimensions)
    },
  )

  const uploadFiles = useCallback(
    (entries: PreparedUploadEntry[]): Array<Promise<void>> =>
      entries.map(async entry => {
        let result: OptFileUploadResult = {url: null, file: entry.file}
        let dimensions: ImageDimensions | null = null
        try {
          // Provide a progress callback to the uploader to track upload progress
          const progressCallback = (progressBytes: number) => {
            const percent = entry.file.size > 0 ? Math.round((progressBytes / entry.file.size) * 100) : 0
            updatePlaceholderProgress(entry.id, Math.min(percent, 99)) // Cap at 99% until complete
          }
          result = (await onUploadFile?.(entry.file, progressCallback)) ?? {file: entry.file, url: null}
        } catch {
          result = {file: entry.file, url: null}
        }
        if (result.url && (entry.mediaKind === 'image' || isImageFile(result.file))) {
          try {
            dimensions = await getImageSizeFromFile(result.file)
          } catch {
            dimensions = {width: 0, height: 0, pixelsPerInch: 1}
            reportError('Failed to get image size from file', {
              message: 'Failed to get image size from file',
              reactAppName: 'markdown-editor',
            })
          }
        }

        // Mark as 100% complete
        updatePlaceholderProgress(entry.id, 100)

        const normalizedUrl = normalizeUploadUrl(result.url)
        const normalizedResult: OptFileUploadResult = normalizedUrl
          ? {url: normalizedUrl, file: result.file}
          : {url: null, file: result.file}

        safeHandleCompletedFileUpload(entry, normalizedResult, dimensions)
      }),
    [onUploadFile, safeHandleCompletedFileUpload, updatePlaceholderProgress],
  )

  const onSelectFiles = useCallback(
    async (accepted: File[], rejected: File[]) => {
      if (accepted.length > 0) {
        const total = accepted.length
        const entries: PreparedUploadEntry[] = accepted.map((file, index) => {
          const id = `upload-placeholder-${placeholderIdRef.current++}`
          const placeholderText = placeholder(file, id)
          const mediaKind = isVideoFile(file) ? 'video' : isImageFile(file) ? 'image' : undefined
          const previewUrl = mediaKind ? URL.createObjectURL(file) : undefined

          return {
            id,
            file,
            placeholderText,
            order: index + 1,
            total,
            previewUrl,
            mediaKind,
          }
        })

        setInitialUploadProgress(total)
        insertPlaceholder(entries)

        await Promise.all(uploadFiles(entries))

        clearUploadProgress()
      }
      // setting rejected files will hide upload progress, replacing it with an error message
      // so only call it after successful files are uploaded
      safeSetRejectedFiles(rejected)
    },
    [safeSetRejectedFiles, insertPlaceholder, uploadFiles, clearUploadProgress, setInitialUploadProgress],
  )

  let fileSelect = useUnifiedFileSelect({
    acceptedFileTypes,
    multi: true,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSelect: onSelectFiles,
  })

  if (disabled || !view) {
    fileSelect = {
      clickTargetProps: {
        onClick: noop,
      },
      dropTargetProps: {
        onDragEnter: noop,
        onDragLeave: noop,
        onDrop: noop,
        onDragOver: noop,
      },
      pasteTargetProps: {
        onPaste: noop,
      },
      isDraggedOver: false,
    }
  }

  return onUploadFile
    ? {
        ...fileSelect,
        errorMessage,
        uploadProgress,
        handleFileUpload: async (file: File) => {
          await onSelectFiles([file], [])
        },
      }
    : null
}
